import apiClient from './config';

/* ───────────────────────── ReportInfo ───────────────────────── */
export interface ReportInfo {
  created_at: string;
  len_data_breaches: number;
  len_spoofeable_domains: number;
  len_emails: number;
  len_ips: number;
  len_subdomains: number;
  len_typosquatting: number;
  len_urls_no_waf: number;
  len_directories: number;
  len_sensible_directories: number;
  len_technologies_url: number;
  len_employees: number;
  len_vulns: number;
  price: number;
  risk: number;
}

/* ──────────────────────── Organization ─────────────────────── */
export interface Organization {
  id: string;
  name: string;
  website_url?: string;
  linkedin_url?: string;
  twitter_url?: string;
  facebook_url?: string;
  phone?: string;
  founded_year?: number;
  logo_url?: string;
  estimated_num_employees?: number;
  primary_domain?: string;
  raw_address?: string;
  city?: string;
  state?: string;
  country?: string;
  clean_description?: string;
  industries: string[];
}

/* ───────────────────────── Employees ───────────────────────── */
export interface Employee {
  email: string;
  first_name?: string;
  last_name?: string;
  title?: string;
  headline?: string;
  score: number;
  kind: string; // abierto para no romper si llegan otros valores
}

export interface ContactedEmployee extends Employee {
  first_contact_at?: string;
  first_email_sent: boolean;
  reminder_email_sent: boolean;
  last_updated_at?: string;
}

/* ───────────────── Items por vista (API shapes) ─────────────── */
export interface OrganizationItem {
  org: Organization;
  report: ReportInfo;
  employees: Employee[];
  total_candidates: number;
}

export interface OrganizationProgressItem {
  org: Organization;
  report: ReportInfo;
  employees_pending: Employee[];
  employees_contacted: ContactedEmployee[];
  total_candidates: number;
}

export interface OrganizationContact {
  email: string;
  first_contact_at?: string;
  first_email_sent: boolean;
  reminder_email_sent: boolean;
  last_updated_at?: string;
  notes?: string;
}

export interface OrganizationContactedItem extends OrganizationItem {
  contacts?: OrganizationContact[];
}

/* ───────────────────── Paginador genérico ───────────────────── */
export interface Page<T> {
  total: number;
  items: T[];
}

/* ───────────────────── Config y helpers ─────────────────────── */
const PAGE_SIZE = 50;

/* ───────────────────────── Endpoints ────────────────────────── */
export const getOrgsToContact = (
  page = 0,
  pageSize = PAGE_SIZE,
  includeAll = false,
) =>
  apiClient
    .get<Page<OrganizationItem>>('/admin/organizations/to_contact', {
      params: {
        limit: pageSize,
        offset: page * pageSize,
        ...(includeAll ? { include_all: 1 } : {}),
      },
    })
    .then((r) => r.data);

export const getOrgsPartial = (
  page = 0,
  pageSize = PAGE_SIZE,
  includeAll = false,
) =>
  apiClient
    .get<Page<OrganizationProgressItem>>('/admin/organizations/partial', {
      params: {
        limit: pageSize,
        offset: page * pageSize,
        ...(includeAll ? { include_all: 1 } : {}),
      },
    })
    // normaliza arrays y campos booleanos para evitar undefined en el UI
    .then((r) => ({
      ...r.data,
      items: (r.data.items ?? []).map((it) => ({
        ...it,
        employees_pending: (it.employees_pending ?? []).map((e) => ({
          ...e,
        })),
        employees_contacted: (it.employees_contacted ?? []).map((c: any) => ({
          ...c,
          first_email_sent:
            typeof c.first_email_sent === 'boolean'
              ? c.first_email_sent
              : Boolean(c.first_contact_at),
          reminder_email_sent: Boolean(c.reminder_email_sent),
        })) as ContactedEmployee[],
        total_candidates:
          typeof it.total_candidates === 'number'
            ? it.total_candidates
            : (it.employees_pending?.length ?? 0) +
              (it.employees_contacted?.length ?? 0),
      })),
    }));

export const getOrgsContacted = (
  page = 0,
  pageSize = PAGE_SIZE,
) =>
  apiClient
    .get<Page<OrganizationContactedItem>>('/admin/organizations/contacted', {
      params: { limit: pageSize, offset: page * pageSize },
    })
    .then((r) => r.data);


export type SendEmailTemplate = 'lead_contact' | 'reminder';

// adminOrganizationsService.ts
export const sendOrganizationEmail = (
  org_id: string,
  emails: string[],
  template: SendEmailTemplate,
) =>
  apiClient
    .post('/admin/organizations/send-email', { org_id, emails, template })
    .then((r) => r.data);


export const finishAllContacts = (org_id: string) =>
  apiClient
    .post(`/admin/organizations/${org_id}/contacts/finish-all`)
    .then((r) => r.data);